import React, { useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import LinkButton from '@atlaskit/button/link';
import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image/image';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { SPACE_DIRECTORY } from '@confluence/named-routes/entry-points/routes.ts';

import starredSpacesEmptyState from './assets/starred-spaces-empty-state.svg';
import starredSpacesEmptyStateDark from './assets/starred-spaces-empty-state-dark.svg';

const SOURCE = 'starredSpacesEmptyState';

type StarredSpacesEmptyStateProps = {
	onExploreSpacesClick: () => void;
};

const styles = cssMap({
	emptyState: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		width: '100%',
	},
	emptyStateContent: {
		width: '100%',
	},
	emptyStateIcon: {
		flexShrink: 0,
		height: '32px',
		overflow: 'visible',
		position: 'relative',
		width: '32px',
	},
	// The telescope + sparkle accent are exported from Figma as a single 41x39 SVG (illustration
	// frame + eyelash overflow). The 32x32 telescope sits at (0,4) within that viewBox, so the
	// image is rendered 1:1 and offset by top:-4px to map the telescope exactly onto the 32x32
	// slot while the accent bleeds above/right of it — one network request instead of two.
	emptyStateImage: {
		height: '39px',
		left: 0,
		position: 'absolute',
		top: token('space.negative.050'),
		width: '41px',
	},
	emptyStateText: {
		flexGrow: 1,
		minWidth: 0,
	},
	emptyStateAction: {
		width: '100%',
	},
});

const i18n = defineMessages({
	emptyStateExploreSpacesDescription: {
		id: 'side-navigation.starred-spaces-list.empty-state-explore-spaces-description',
		defaultMessage: 'Star spaces to keep them here for quick access.',
		description:
			'Description of the Mohiti empty state shown when there are user-created spaces but none have been starred.',
	},
	exploreSpaces: {
		id: 'side-navigation.starred-spaces-list.empty-state-explore-spaces',
		defaultMessage: 'Explore spaces',
		description: 'Primary action in the Mohiti empty state that links to the spaces directory.',
	},
});

export const StarredSpacesEmptyState = ({
	onExploreSpacesClick,
}: StarredSpacesEmptyStateProps): React.JSX.Element => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'starredSpacesEmptyStateScreen',
				source: SOURCE,
				attributes: {
					navVersion: '4',
				},
			},
		}).fire();
	}, [createAnalyticsEvent]);

	return (
		<Box paddingBlock="space.150" paddingInline="space.150" xcss={styles.emptyState}>
			<Stack alignInline="center" space="space.100">
				<Inline
					alignBlock="center"
					space="space.200"
					shouldWrap={false}
					xcss={styles.emptyStateContent}
				>
					<Box xcss={styles.emptyStateIcon}>
						<Box xcss={styles.emptyStateImage}>
							<Image
								src={starredSpacesEmptyState}
								srcDark={starredSpacesEmptyStateDark}
								alt=""
								height="39px"
								width="41px"
							/>
						</Box>
					</Box>
					<Box xcss={styles.emptyStateText}>
						<Text color="color.text">
							<FormattedMessage {...i18n.emptyStateExploreSpacesDescription} />
						</Text>
					</Box>
				</Inline>
				<Box xcss={styles.emptyStateAction}>
					<LinkButton
						spacing="compact"
						shouldFitContainer
						href={SPACE_DIRECTORY.toUrl()}
						onClick={onExploreSpacesClick}
					>
						{intl.formatMessage(i18n.exploreSpaces)}
					</LinkButton>
				</Box>
			</Stack>
		</Box>
	);
};
